import {useCallback, useEffect, useMemo, useState} from 'react'
import {CircularProgress, Typography} from '@mui/material'
import {useTheme} from '@mui/material/styles'
import Highcharts from 'highcharts'
import HighchartsReact from 'highcharts-react-official'
import moment, {Moment} from 'moment'
import CardCustom from 'src/components/elements/CardCustom/CardCustom'
import LayoutContainer from 'src/components/layouts/LayoutContainer/LayoutContainer'
import TabsLayout from 'src/components/layouts/TabsLayout/TabsLayout'
import Filters, {FilterItem} from 'src/components/templates/Filters/Filters'
import {PATHS, STATUS} from 'src/constants/constants'
import {useAppSelector} from 'src/hooks'
import apiClient from 'src/services/apiClient'
import {
  ADHERENCE_DEFAULT_RANGE_DAYS,
  AdherenceFilters,
  AdherenceLabels,
  AdherenceResponse,
  AdherenceSection,
  AdherenceTherapyType,
  buildAdherenceByTherapyOptions,
  buildAdherenceFilterItems,
  buildAdherenceTimelineOptions,
  formatAdherencePercentage,
  isSecondaryAdherenceEnabled,
  resolveAdherenceFiltersConfig,
  resolveAdherenceLabels,
  resolveAdherenceTabs,
  toAdherenceFilters,
} from 'src/utils/adherenceCharts'
import {AnalyticsStyled} from './AnalyticsStyled'

type PerTab<T> = Record<AdherenceTherapyType, T>

// Same window the backend falls back to, made explicit so the "Data" filter shows it pre-filled
// and "Annulla" brings it back instead of clearing to an unbounded range.
function defaultRange(): [Moment, Moment] {
  return [moment().subtract(ADHERENCE_DEFAULT_RANGE_DAYS, 'days'), moment()]
}

function defaultFilters(): AdherenceFilters {
  const [from, to] = defaultRange()
  return {dateFrom: from.startOf('day').toISOString(), dateTo: to.endOf('day').toISOString()}
}

function SectionContent({section, granularity, labels}: {
  section: AdherenceSection
  granularity: AdherenceResponse['granularity']
  labels: AdherenceLabels
}) {
  const theme = useTheme()
  const color = theme.palette.primary.main

  if (section.total === 0) {
    return (
      <Typography className='emptyMessage' variant='body2'>
        Non ci sono assunzioni previste per i filtri selezionati.
      </Typography>
    )
  }

  const totals = [
    {label: labels.adherence, value: formatAdherencePercentage(section.percentage)},
    {label: labels.completedAssumptions, value: section.completed},
    {label: labels.expectedAssumptions, value: section.total},
  ]

  return (
    <div className='sectionContent'>
      <CardCustom title={<Typography fontWeight={700} variant='body2'>{labels.totalTitle}</Typography>}>
        <div className='totals'>
          {totals.map(item => (
            <div className='totalItem' key={item.label}>
              <Typography variant='caption'>{item.label}</Typography>
              <Typography fontWeight={700} variant='h4'>{item.value}</Typography>
            </div>
          ))}
        </div>
      </CardCustom>
      <div className='charts'>
        <CardCustom title={<Typography fontWeight={700} variant='body2'>{labels.timelineTitle}</Typography>}>
          <HighchartsReact highcharts={Highcharts} options={buildAdherenceTimelineOptions(section, granularity, color)} />
        </CardCustom>
        <CardCustom title={<Typography fontWeight={700} variant='body2'>{labels.byTherapyTitle}</Typography>}>
          <HighchartsReact highcharts={Highcharts} options={buildAdherenceByTherapyOptions(section, color)} />
        </CardCustom>
      </div>
    </div>
  )
}

// Analytics page of the patient / insurance user: adherence to their primary and secondary
// therapies, i.e. COMPLETED assumptions over every assumption due in the selected range.
function Analytics () {
  const label = useAppSelector(state => state.pspConfigReducer.data.sidebarItems?.analytics?.label) ?? 'Analytics'
  const analyticsConfigurations = useAppSelector(state => state.pspConfigReducer.data.analyticsConfigurations)
  const configuredFilters = analyticsConfigurations?.filtersConfig
  const sectionsConfig = analyticsConfigurations?.sections
  const labels = useMemo(() => resolveAdherenceLabels(analyticsConfigurations?.labels), [analyticsConfigurations?.labels])
  const secondaryEnabled = isSecondaryAdherenceEnabled(sectionsConfig)
  // Each tab has its own filters (and its own Filters drawer below): the drugs of the primary
  // therapies are not those of the secondary ones, so a drug picked on one tab must not carry over.
  const [filtersByTab, setFiltersByTab] = useState<PerTab<AdherenceFilters>>(() => ({primary: defaultFilters(), secondary: defaultFilters()}))
  // options of each tab's "Farmaco" filter, from its last response
  const [drugNamesByTab, setDrugNamesByTab] = useState<PerTab<string[]>>({primary: [], secondary: []})
  // tagged with the tab it was fetched for, so switching tab never shows the other one's numbers
  const [data, setData] = useState<{therapyType: AdherenceTherapyType; response: AdherenceResponse}>()
  const [activeTab, setActiveTab] = useState<AdherenceTherapyType>('primary')
  const [status, setStatus] = useState<string>(STATUS.PENDING)
  // undefined while still unknown
  const [hasSecondaryTherapies, setHasSecondaryTherapies] = useState<boolean>()

  useEffect(() => {
    // A disabled section is never shown, whatever the patient has
    if (!secondaryEnabled) {
      setHasSecondaryTherapies(false)
      return
    }
    // Same rule as TherapyDiary: the "Terapie Accessorie" tab only exists for a patient who has some.
    apiClient.getSecondaryTherapies(() => {})
      .then((therapies: unknown) => setHasSecondaryTherapies(Array.isArray(therapies) && therapies.length > 0))
      .catch(() => setHasSecondaryTherapies(false))
  }, [secondaryEnabled])

  // Only the sections enabled in the configuration (Terapie Accessorie also needs some therapies)
  const tabs = useMemo(() => resolveAdherenceTabs(sectionsConfig, Boolean(hasSecondaryTherapies)), [sectionsConfig, hasSecondaryTherapies])
  const isActiveTabShown = tabs.some(tab => tab.type === activeTab)

  useEffect(() => {
    if (!isActiveTabShown && tabs.length > 0) setActiveTab(tabs[0].type)
  }, [isActiveTabShown, tabs])

  const filters = filtersByTab[activeTab]

  // One endpoint per tab: only the tab on screen is loaded.
  const fetchAdherence = useCallback(() => {
    if (!isActiveTabShown) return
    const fetcher = activeTab === 'secondary' ? apiClient.getSecondaryTherapiesAdherence : apiClient.getTherapiesAdherence
    fetcher(filters, setStatus)
      .then((response: AdherenceResponse) => {
        setData({therapyType: activeTab, response})
        setDrugNamesByTab(prev => ({...prev, [activeTab]: response.drugNames ?? []}))
      })
      .catch(() => setData(undefined))
  }, [filters, activeTab, isActiveTabShown])

  const section = data?.therapyType === activeTab ? data.response : undefined

  useEffect(() => {
    fetchAdherence()
  }, [fetchAdherence])

  const visibleFilters = useMemo(() => resolveAdherenceFiltersConfig(configuredFilters), [configuredFilters])
  const filterItemsByTab: PerTab<FilterItem[]> = useMemo(() => ({
    primary: buildAdherenceFilterItems(visibleFilters, defaultRange(), drugNamesByTab.primary),
    secondary: buildAdherenceFilterItems(visibleFilters, defaultRange(), drugNamesByTab.secondary),
  }), [visibleFilters, drugNamesByTab])

  const handleFiltersSubmit = (tab: AdherenceTherapyType, results: any[]) => {
    setFiltersByTab(prev => ({...prev, [tab]: toAdherenceFilters(results)}))
  }

  // every section disabled (or only Terapie Accessorie, for a patient who has none)
  const noSections = tabs.length === 0 && hasSecondaryTherapies !== undefined

  // Both tabs render the same content: only the active one is visible, and `section` is the active
  // one's data.
  const renderSection = () => {
    if (!isActiveTabShown || status === STATUS.PENDING) return <div className='loader'><CircularProgress /></div>
    if (status === STATUS.REJECTED) {
      return <Typography color='error' className='emptyMessage' variant='body2'>Impossibile caricare l&apos;aderenza alla terapia.</Typography>
    }
    return section ? <SectionContent granularity={section.granularity} section={section} labels={labels} /> : null
  }

  return (
    <LayoutContainer breadcrumbs={[{label, href: PATHS.ANALYTICS}]}>
      <AnalyticsStyled>
        <div className='toolbar'>
          <Typography variant='body2'>
            {section ? `Periodo: ${moment(section.dateFrom).format('DD/MM/YYYY')} – ${moment(section.dateTo).format('DD/MM/YYYY')}` : ''}
          </Typography>
          {visibleFilters.length > 0 && tabs.map(({type}) => (
            // the inactive tab's drawer stays mounted, so its selection is still there when coming back
            <div key={type} style={{display: type === activeTab ? 'block' : 'none'}}>
              <Filters
                formId={`adherence-filter-form-${type}`}
                config={filterItemsByTab[type]}
                onSubmit={(results: any[]) => handleFiltersSubmit(type, results)}
              />
            </div>
          ))}
        </div>
        {noSections && (
          <Typography className='emptyMessage' variant='body2'>Non ci sono sezioni di aderenza disponibili.</Typography>
        )}
        {!noSections && tabs.length === 0 && <div className='loader'><CircularProgress /></div>}
        {tabs.length > 0 && <TabsLayout
          // TabsLayout reads its tabs only at mount: remount once the shown sections are known.
          key={tabs.map(tab => tab.type).join('-')}
          contents={tabs.map(() => renderSection())}
          onChange={(index: number) => setActiveTab(tabs[index]?.type ?? tabs[0].type)}
          tabs={tabs.map(tab => ({label: tab.label}))}
        />}
      </AnalyticsStyled>
    </LayoutContainer>
  )
}

export default Analytics
